import {
  Button,
  IconButton,
  IconButtonProps,
  Tooltip,
} from '@wix/design-system';
import React, { useEffect } from 'react';
import { ToolbarCollectionState } from '../../state';
import { FiltersMap, withoutDefaults } from '@wix/bex-core';
import { observer } from 'mobx-react-lite';
import { AdjustSmall, TableChartSmall } from '@wix/wix-ui-icons-common';
import { CustomColumnsPanelEntry } from './CustomColumnsPanelEntry';
import type { CustomColumnsDragAndDrop } from '../CustomColumns/CustomColumnsDragAndDrop';
import type { Column } from '../../model';
import { cairoFiltersPanelUsed } from '@wix/bex-core/bi';

export interface CustomColumnsCTAButtonProps<T, F extends FiltersMap> {
  state: ToolbarCollectionState<T, F>;
  onColumnClick?: (column: Column, checked: boolean) => unknown;
  dragAndDrop?: typeof CustomColumnsDragAndDrop;
}

export function _CustomColumnsCTAButton<T, F extends FiltersMap>({
  state,
  onColumnClick,
  dragAndDrop,
}: CustomColumnsCTAButtonProps<T, F>) {
  const {
    container: { translate: t, experiments },
  } = state;

  const analyticsButtonDiscoverability = experiments?.get(
    'specs.analytics.CustomColumnsWithLabelAndNewIcon',
  );

  useEffect(() => {
    CustomColumnsPanelEntry.CustomColumnsDragAndDrop = dragAndDrop;
    state.panels.registerSidePanel('customColumns', CustomColumnsPanelEntry);
  }, []);

  useEffect(() => {
    if (state.customColumnsState) {
      state.customColumnsState.onColumnClick = onColumnClick;
    }
  }, [onColumnClick, state.customColumnsStateInitialized]);

  const text = t('cairo.table.custom-columns.button.cta');
  const props: IconButtonProps = {
    size: 'small',
    priority: 'secondary',
    dataHook: 'custom-columns-button',
    onClick: () => {
      const isOpened = state.panels.toggleSidePanel('customColumns');

      state.reportBi(
        withoutDefaults(cairoFiltersPanelUsed)({
          isOpened,
          feature: 'Custom Columns',
          origin: 'Custom Columns CTA',
        }),
      );
    },
  };

  const getButton = () => {
    if (
      analyticsButtonDiscoverability === 'b' ||
      analyticsButtonDiscoverability === 'c'
    ) {
      return (
        <Button
          {...props}
          prefixIcon={
            analyticsButtonDiscoverability === 'b' ? (
              <AdjustSmall />
            ) : (
              <TableChartSmall />
            )
          }
        >
          {text}
        </Button>
      );
    }

    return (
      <Tooltip content={t('cairo.table.custom-columns.button.tooltip')}>
        <IconButton {...props}>
          <AdjustSmall />
        </IconButton>
      </Tooltip>
    );
  };

  return getButton();
}

export const CustomColumnsCTAButton = observer(_CustomColumnsCTAButton);
